// src/reasoningTests/results/ReasoningTestResult.js
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const ReasoningTestResult = ({ test }) => {
    const { t, i18n } = useTranslation();
    const { results, aiAnalysis, testType } = test;
    const language = test.language || i18n.language || 'es';

    if (!results) {
        return (
            <Typography variant="body2" color="text.secondary">
                {t('reasoningTests.noResults')}
            </Typography>
        );
    }

    const {
        correctTotal = 0,
        correctResponded = 0,
        questionsAnswered = 0,
        respondedPercent = 0,
        timeReal = 0,
        timeTotal = 0,
        correct = 0,
        total = 0
    } = results;

    const confidenceIndex = test.confidenceIndex;

    // Formatear tiempo
    const formatTime = (ms) => {
        if (!ms || isNaN(ms)) {
            return '0:00';
        }
        const totalSeconds = Math.floor(ms / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}:${String(seconds).padStart(2, '0')}`;
    };

    // Obtener texto de nivel de confiabilidad
    const getConfidenceText = (index) => {
        if (index >= 90) return t('reasoningTests.confidenceHigh');
        if (index >= 70) return t('reasoningTests.confidenceMedium');
        if (index >= 50) return t('reasoningTests.confidenceLow');
        return t('reasoningTests.confidenceVeryLow');
    };

    // Obtener título del test según el tipo
    const getTestTitle = () => {
        const titles = {
            'wordAssociation': t('reasoningTests.wordAssociation'),
            'logicalProcess': t('reasoningTests.logicalProcess'),
            'numericalReasoning': t('reasoningTests.numericalReasoning'),
            'socialContext': t('reasoningTests.socialContext'),
            'logicalAbstract': t('reasoningTests.logicalAbstract'),
        };
        return titles[testType] || t('reasoningTests.title');
    };

    return (
        <Box>
            {/* Header con título */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {getTestTitle()}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {t('reasoningTests.resultsTitle')}
                </Typography>
                <Typography variant="h6" color="text.secondary">
                    {test.applicantName}
                </Typography>
            </Box>

            {/* Tabla de detalles técnicos */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 3,
                    border: '2px solid #e0e0e0',
                    borderRadius: 2
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                    {t('reasoningTests.technicalDetails')}
                </Typography>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('reasoningTests.metric')}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('reasoningTests.value')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {testType === 'integrityTest' ? (
                                <>
                                    <TableRow>
                                        <TableCell>{t('reasoningTests.integrityIGI')}</TableCell>
                                        <TableCell align="right">{results.IGI || 0}/100</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>{t('reasoningTests.integrityRiskLevel')}</TableCell>
                                        <TableCell align="right">
                                            <Typography
                                                sx={{
                                                    color: results.riskLevel === 'low' ? 'success.main' :
                                                        results.riskLevel === 'medium' ? 'warning.main' : 'error.main',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {results.riskLevel === 'low' ? t('reasoningTests.integrityLowRisk') :
                                                    results.riskLevel === 'medium' ? t('reasoningTests.integrityMediumRisk') :
                                                        t('reasoningTests.integrityHighRisk')}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>{t('reasoningTests.questionsAnswered')}</TableCell>
                                        <TableCell align="right">{questionsAnswered} / {total}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>{t('reasoningTests.answeredPercent')}</TableCell>
                                        <TableCell align="right">{respondedPercent}%</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>{t('reasoningTests.integrityValidResponses')}</TableCell>
                                        <TableCell align="right">
                                            {results.validityCheck?.isValid ?
                                                t('reasoningTests.integrityValid') :
                                                t('reasoningTests.integrityQuestionable')}
                                        </TableCell>
                                    </TableRow>
                                    {confidenceIndex !== undefined && confidenceIndex !== null && (
                                        <TableRow>
                                            <TableCell>{t('reasoningTests.confidenceIndex')}</TableCell>
                                            <TableCell align="right">
                                                {confidenceIndex.toFixed(1)}% ({getConfidenceText(confidenceIndex)})
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </>
                            ) : testType === 'emotionalIntelligence' ? (
                                <>
                                    <TableRow>
                                        <TableCell>{t('reasoningTests.emotionalCIE')}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                                            {results.CIE || 0}%
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>{t('reasoningTests.emotionalLevel')}</TableCell>
                                        <TableCell align="right">
                                            <Typography
                                                sx={{
                                                    color: results.level === 'high' ? 'success.main' :
                                                        results.level === 'medium' ? 'warning.main' : 'error.main',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {results.level === 'high' ? t('reasoningTests.emotionalHigh') :
                                                    results.level === 'medium' ? t('reasoningTests.emotionalMedium') :
                                                        t('reasoningTests.emotionalLow')}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>{t('reasoningTests.questionsAnswered')}</TableCell>
                                        <TableCell align="right">{questionsAnswered} / {total}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>{t('reasoningTests.answeredPercent')}</TableCell>
                                        <TableCell align="right">{respondedPercent}%</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>{t('reasoningTests.realTime')}</TableCell>
                                        <TableCell align="right">{formatTime(timeReal)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>{t('reasoningTests.totalTime')}</TableCell>
                                        <TableCell align="right">{formatTime(timeTotal)}</TableCell>
                                    </TableRow>
                                    {confidenceIndex !== undefined && confidenceIndex !== null && (
                                        <TableRow>
                                            <TableCell>{t('reasoningTests.confidenceIndex')}</TableCell>
                                            <TableCell align="right">
                                                {confidenceIndex.toFixed(1)}% ({getConfidenceText(confidenceIndex)})
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </>
                            ) : (
                                <>
                                    <TableRow>
                                        <TableCell>{t('reasoningTests.correctTotal')}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                                            {Math.round((correctTotal / 100) * total)}/{total} ({correctTotal}%)
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>{t('reasoningTests.correctResponded')}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                                            {Math.round((correctResponded / 100) * questionsAnswered)}/{questionsAnswered} ({correctResponded}%)
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>{t('reasoningTests.questionsAnswered')}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                                            {questionsAnswered}/{total}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>{t('reasoningTests.answeredPercent')}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                                            {respondedPercent}%
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>{t('reasoningTests.realTime')}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                                            {formatTime(timeReal)}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>{t('reasoningTests.totalTime')}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                                            {formatTime(timeTotal)}
                                        </TableCell>
                                    </TableRow>
                                    {confidenceIndex !== undefined && confidenceIndex !== null && (
                                        <TableRow>
                                            <TableCell>{t('reasoningTests.confidenceIndex')}</TableCell>
                                            <TableCell align="right" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                                                {confidenceIndex.toFixed(1)}% ({getConfidenceText(confidenceIndex)})
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Tabla de dimensiones para Inteligencia Emocional */}
            {testType === 'emotionalIntelligence' && results.dimensionScores && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        mb: 3,
                        border: '2px solid #e0e0e0',
                        borderRadius: 2
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                        {t('reasoningTests.emotionalDimensions')}
                    </Typography>

                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                        {language === 'es' ? 'Dimensión' : 'Dimension'}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                        {language === 'es' ? 'Puntaje' : 'Score'}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.entries(results.dimensionScores).map(([dimension, score]) => (
                                    <TableRow key={dimension}>
                                        <TableCell>{dimension}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                            {score}%
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* Análisis de IA si existe */}
            {aiAnalysis && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        mb: 3,
                        border: '2px solid #e0e0e0',
                        borderRadius: 2
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                        {t('reasoningTests.performanceInterpretation')}
                    </Typography>

                    {(() => {
                        try {
                            // ✅ Limpieza robusta del aiAnalysis
                            let cleanedAnalysis = aiAnalysis;

                            if (typeof aiAnalysis === 'string') {
                                // 1. Remover caracteres de control (excepto \n, \r, \t que son válidos)
                                cleanedAnalysis = cleanedAnalysis.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');

                                // 2. Limpiar ] extras dentro de los valores de las propiedades
                                cleanedAnalysis = cleanedAnalysis.replace(/\.\]\s*"\s*}/g, '." }');

                                // 3. Remover ] extra al final si existe
                                cleanedAnalysis = cleanedAnalysis.trim();
                                if (cleanedAnalysis.endsWith(']}')) {
                                    const lastBraceIndex = cleanedAnalysis.lastIndexOf('}');
                                    if (lastBraceIndex !== -1) {
                                        cleanedAnalysis = cleanedAnalysis.substring(0, lastBraceIndex + 1);
                                    }
                                } else if (cleanedAnalysis.endsWith(']')) {
                                    cleanedAnalysis = cleanedAnalysis.slice(0, -1).trim();
                                }

                                // 4. Normalizar espacios en blanco
                                cleanedAnalysis = cleanedAnalysis.replace(/\s+/g, ' ');
                            }

                            const analysis = typeof cleanedAnalysis === 'string' ?
                                JSON.parse(cleanedAnalysis) : cleanedAnalysis;

                            return (
                                <Box>
                                    {/* Análisis General */}
                                    {(analysis.analisisGeneral || analysis.generalAnalysis) && (
                                        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, textAlign: 'justify' }}>
                                            {analysis.analisisGeneral || analysis.generalAnalysis}
                                        </Typography>
                                    )}

                                    {/* Fortalezas */}
                                    {(analysis.fortalezas || analysis.strengths) && (
                                        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, textAlign: 'justify' }}>
                                            {analysis.fortalezas || analysis.strengths}
                                        </Typography>
                                    )}

                                    {/* Áreas de Desarrollo */}
                                    {(analysis.areasDesarrollo || analysis.developmentAreas) && (
                                        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, textAlign: 'justify' }}>
                                            {analysis.areasDesarrollo || analysis.developmentAreas}
                                        </Typography>
                                    )}

                                    {/* Recomendaciones */}
                                    {(analysis.recomendaciones || analysis.recommendations) && (
                                        <Typography variant="body1" sx={{ lineHeight: 1.8, textAlign: 'justify' }}>
                                            {analysis.recomendaciones || analysis.recommendations}
                                        </Typography>
                                    )}
                                </Box>
                            );
                        } catch (error) {
                            console.error('Error parseando aiAnalysis:', error);
                            console.error('aiAnalysis original:', aiAnalysis);

                            // Si falla el parsing, mostrar como texto plano
                            return (
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
                                    {typeof aiAnalysis === 'string' ?
                                        aiAnalysis : JSON.stringify(aiAnalysis, null, 2)}
                                </Typography>
                            );
                        }
                    })()}
                </Paper>
            )}
        </Box>
    );
};

export default ReasoningTestResult;